/**
 * Colour maths, so the theme editor computes what it claims.
 *
 * Whether a colour is readable, and whether two colours stay apart for a
 * colour-blind reader, are measurable — they are not a matter of taste and
 * must never be eyeballed. Everything here works in OKLab/OKLCH, where equal
 * numeric steps look like equal steps, which is what makes a generated ramp
 * from one picked colour look deliberate rather than muddy.
 *
 * The CVD transforms are Machado, Oliveira & Fernandes (2009) at severity 1.0,
 * and distances are Euclidean in OKLab × 100.
 */

export interface Oklch {
  /** Perceived lightness, 0 (black) to 1 (white). */
  l: number;
  /** Colourfulness. Below about 0.03 a hue reads as grey. */
  c: number;
  /** Hue angle in degrees. */
  h: number;
}

const MACHADO = {
  protan: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deutan: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
} as const;

export type Deficiency = keyof typeof MACHADO;

const clamp = (value: number, low = 0, high = 1): number =>
  Math.min(high, Math.max(low, value));

export function isHex(value: string): boolean {
  return /^#?[0-9a-fA-F]{6}$/.test(value.trim());
}

export function normaliseHex(value: string): string {
  return `#${value.trim().replace(/^#/, '').toLowerCase()}`;
}

function hexToSrgb(hex: string): [number, number, number] {
  const body = hex.trim().replace(/^#/, '');
  return [0, 2, 4].map((index) => parseInt(body.slice(index, index + 2), 16) / 255) as [
    number,
    number,
    number,
  ];
}

const toLinear = (channel: number): number =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

const toSrgb = (channel: number): number => {
  const value = clamp(channel);
  return value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055;
};

function linearOf(hex: string): [number, number, number] {
  return hexToSrgb(hex).map(toLinear) as [number, number, number];
}

function oklabFromLinear([r, g, b]: [number, number, number]): [number, number, number] {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function linearFromOklab([L, a, b]: [number, number, number]): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

export function hexToOklch(hex: string): Oklch {
  const [L, a, b] = oklabFromLinear(linearOf(hex));
  const hue = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l: L, c: Math.hypot(a, b), h: hue };
}

/**
 * The nearest colour a screen can actually show.
 *
 * An OKLCH triple can name a colour outside sRGB; chroma is reduced until it
 * fits rather than clipping the channels, which would shift the hue.
 */
export function oklchToHex({ l, c, h }: Oklch): string {
  const radians = (h * Math.PI) / 180;

  for (let chroma = c; chroma >= 0; chroma -= 0.005) {
    const rgb = linearFromOklab([
      clamp(l),
      chroma * Math.cos(radians),
      chroma * Math.sin(radians),
    ]);
    if (rgb.every((channel) => channel >= -0.0001 && channel <= 1.0001)) {
      return rgbToHex(rgb);
    }
  }

  return rgbToHex(linearFromOklab([clamp(l), 0, 0]));
}

function rgbToHex(linear: [number, number, number]): string {
  return `#${linear
    .map((channel) =>
      Math.round(toSrgb(channel) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrast(a: string, b: string): number {
  const luminance = (hex: string): number => {
    const [r, g, bb] = linearOf(hex);
    return 0.2126 * r + 0.7152 * g + 0.0722 * bb;
  };

  const [high, low] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (high + 0.05) / (low + 0.05);
}

function simulate(hex: string, kind: Deficiency): [number, number, number] {
  const [r, g, b] = linearOf(hex);
  const matrix = MACHADO[kind];
  return [
    clamp(matrix[0][0] * r + matrix[0][1] * g + matrix[0][2] * b),
    clamp(matrix[1][0] * r + matrix[1][1] * g + matrix[1][2] * b),
    clamp(matrix[2][0] * r + matrix[2][1] * g + matrix[2][2] * b),
  ];
}

/** How far apart two colours look, in OKLab × 100. Under 8 is too close. */
export function deltaE(a: string, b: string, kind?: Deficiency): number {
  const first = oklabFromLinear(kind ? simulate(a, kind) : linearOf(a));
  const second = oklabFromLinear(kind ? simulate(b, kind) : linearOf(b));

  return (
    100 *
    Math.hypot(first[0] - second[0], first[1] - second[1], first[2] - second[2])
  );
}

/** The worst case across the deficiencies that affect most people. */
export function worstDeltaE(a: string, b: string): number {
  return Math.min(deltaE(a, b, 'protan'), deltaE(a, b, 'deutan'));
}
